import { isValidPhoneNumber } from "libphonenumber-js";
import { z } from "zod";

// Validation for person-detail values, shared by the in-line import review AND the normal profile
// editor (SAVE · Stammdaten). The caps mirror the constants in the genesis `ImportedProfileSanitizer`,
// which is the only place genesis states them — and it runs on the IMPORT path alone. On the normal
// save path genesis validates nothing: `updatePerson` has no data validation, and every column below
// is unbounded `text` except `person_detail.bio` (varchar(65535), whose overflow surfaces only as the
// opaque top-level error "Persistence error"). So for the editor these caps are not a mirror of a
// server rule — they ARE the rule, and the client is the only place that can hold it.
//
// Kept in sync BY HAND — the client can't import the Java constants; changing a cap in
// `ImportedProfileSanitizer` means changing it here too.
//
// Deliberately absent: `miscellaneous`, `currentTasks` and `countryState`. genesis defines no cap for
// those anywhere, not even on the import path, so spark does not invent one.
export const IMPORT_FIELD_MAX_CHARS: Record<string, number> = {
  bio: 65535,
  profession: 128,
  jobTitle: 128,
  currentEmployer: 128,
  street: 128,
  phone: 32,
  email: 512,
  zipCode: 16,
  city: 256,
  nationality: 64,
  houseNumber: 16,
  apartment: 16,
  drivingLicence: 64,
  website: 128,
  currentActivity: 65536, // CURRENT_ACTIVITY_MAX_CHARS — genesis's own value, one more than bio's 65535
};

/**
 * Length caps for the COLLECTION row fields, keyed by the review's own vocabulary (`title`,
 * `fieldOfStudy`, `skill`, …), which differs from the editor's descriptor keys. Same genesis constants
 * behind both: EXPERIENCE_JOB_TITLE/EMPLOYER/LOCATION 128, EDUCATION_INSTITUTION/FIELD 256,
 * CERTIFICATE_NAME 128, EXPERTISE_DESCRIPTION 64 — all import-path-only in genesis, so as with the
 * person_detail caps these are the only ceiling that exists.
 *
 * Deliberately absent: the experience row's `description`. It is a derived join of the activities list
 * (ProfileImportDiffService `descriptionOf`), so a cap on the joined text would mean something
 * different from the per-line cap the editor applies. The editor, where the lines still exist, owns it.
 */
export const ROW_FIELD_MAX_CHARS: Readonly<Record<string, Readonly<Record<string, number>>>> = {
  experiences: { title: 128, employer: 128, location: 128 },
  educations: { institution: 256, fieldOfStudy: 256 },
  certificates: { name: 128 },
  expertises: { skill: 64 },
};

/** The cap for one collection row field, or undefined when the field is uncapped. */
export function rowFieldMaxChars(section: string, field: string): number | undefined {
  return ROW_FIELD_MAX_CHARS[section]?.[field];
}

/** The length error for one collection row field value, or null. */
export function rowFieldError(section: string, field: string, value: string | null | undefined): ImportFieldError | null {
  const cap = rowFieldMaxChars(section, field);
  if (cap === undefined || value == null) return null;
  return value.length > cap ? "too-long" : null;
}

export type ImportFieldError =
  | "too-long"
  | "invalid-email"
  | "invalid-phone"
  | "invalid-url"
  | "child-count-max"
  | "child-count-negative";

const EMAIL = z.email();

// Tolerant host shape for `website`. genesis has no url validation anywhere and stores whatever
// arrives, so a scheme is not required: users type "smycles.com" as readily as
// "https://smycles.com/team". What this rejects is a value that cannot be a host at all — embedded
// whitespace, no dot-separated TLD, a userinfo `@`, or a non-http(s) scheme.
const URL_LIKE = /^(?:https?:\/\/)?[^\s/?#@:]+\.[^\s/?#@:]{2,}(?:[/?#]\S*)?$/i;

// person_detail.child_count has no DB upper bound; the profile editor caps it at 20
// (ProfilePage `childCountOverMax`), so the import review mirrors that same ceiling.
export const CHILD_COUNT_MAX = 20;

/**
 * The error for a single person-detail field value, or null when it's fine. A blank value is never an
 * error (an empty value isn't committed). Length is checked for every capped field; `email` must be a
 * valid address; `website` must look like a host ({@link URL_LIKE}); `phone` must be a valid number
 * *when it carries a country code* — a national-format number (no `+`) can't be validated without a
 * region, so it's left alone, matching L1's fail-open; `childCount` may not exceed
 * {@link CHILD_COUNT_MAX}.
 */
export function importFieldError(key: string, value: string | null | undefined): ImportFieldError | null {
  if (value == null || value.trim() === "") return null;
  const cap = IMPORT_FIELD_MAX_CHARS[key];
  if (cap !== undefined && value.length > cap) return "too-long";
  const trimmed = value.trim();
  if (key === "email" && !EMAIL.safeParse(trimmed).success) return "invalid-email";
  if (key === "website" && !URL_LIKE.test(trimmed)) return "invalid-url";
  if (key === "phone" && trimmed.startsWith("+") && !isValidPhoneNumber(trimmed)) return "invalid-phone";
  if (key === "childCount") {
    // `min={0}` on an input outside a <form> does not stop the user typing -3, and genesis has no
    // lower bound either, so the range is checked on both sides here.
    const count = Number.parseInt(trimmed, 10);
    if (count < 0) return "child-count-negative";
    if (count > CHILD_COUNT_MAX) return "child-count-max";
  }
  return null;
}
